import * as React from 'react';

import { useIntl } from 'react-intl';

import { tableMessages } from 'src/registry/components/table/utils/messages';
import { Input } from 'src/registry/ui/Input';
import SingleDatePicker from 'src/registry/ui/SingleDatePicker';
import { storage } from 'src/registry/utils/storage';
import { cn } from 'src/utils/shadcn';

type DataTableDateDropdownProps = {
    className?: string;
    afterDate: Date;
    beforeDate: Date;
    setValue: ({ afterDate, beforeDate }: { afterDate: Date; beforeDate: Date }) => void;
    setLabel?: (label: string) => void;
    enableTime?: boolean;
    inline?: boolean;
};
export default function DataTableDateDropdown({
    setValue,
    setLabel,
    enableTime,
    inline,
    beforeDate,
    afterDate,
    className,
}: DataTableDateDropdownProps) {
    const { formatMessage } = useIntl();
    const locale = storage.getLanguage();

    const formatDate = React.useCallback((date: Date) => {
        return date
            .toLocaleDateString(locale, {
                year: 'numeric',
                month: 'numeric',
                day: '2-digit',
            })
            .replace('г.', '');
    }, []);

    const [{ defaultBeforeTime, defaultAfterTime }] = React.useState(() => {
        return {
            defaultBeforeTime: `${beforeDate.getHours().toString().padStart(2, '0')}:${beforeDate.getMinutes().toString().padStart(2, '0')}`,
            defaultAfterTime: `${afterDate.getHours().toString().padStart(2, '0')}:${afterDate.getMinutes().toString().padStart(2, '0')}`,
        };
    });

    return (
        <div className={cn('flex flex-row gap-1 px-2', className)}>
            <div className="flex w-full flex-col items-start justify-between gap-2 text-end">
                {!inline && <span className="w-full text-left text-sm">{formatMessage(tableMessages.from)}</span>}
                <SingleDatePicker
                    className="w-full"
                    buttonValue={formatDate(afterDate)}
                    value={new Date(afterDate.getTime() - afterDate.getTimezoneOffset() * 60 * 1000)}
                    onChange={(date) => {
                        if (!date) return;

                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);

                        utcDate.setHours(afterDate.getHours(), afterDate.getMinutes());

                        let newBeforeDate = beforeDate;
                        if (utcDate.getTime() > beforeDate.getTime()) {
                            newBeforeDate = new Date(utcDate);
                        }

                        setValue({ afterDate: utcDate, beforeDate: newBeforeDate });
                        if (!setLabel) return;
                        setLabel(`${formatDate(utcDate)} - ${formatDate(beforeDate)}`);
                    }}
                />
                {enableTime && (
                    <Input
                        type="time"
                        className="w-full justify-center p-2 text-sm"
                        defaultValue={defaultAfterTime}
                        onChange={(e) => {
                            const [hours, minutes] = e.target.value.split(':').map(Number);
                            const newAfterDate = new Date(afterDate);
                            newAfterDate.setHours(hours, minutes);

                            if (newAfterDate.getTime() > beforeDate.getTime()) return;

                            setValue({ beforeDate, afterDate: newAfterDate });
                            if (!setLabel) return;
                            setLabel(`${formatDate(afterDate)} - ${formatDate(beforeDate)}`);
                        }}
                    />
                )}
            </div>

            {inline ? (
                <span className="flex items-center px-2 text-sm">{formatMessage(tableMessages.to)}</span>
            ) : (
                <div className="flex flex-col items-center justify-center">
                    <span className="text-sm" contentEditable={true} />
                </div>
            )}

            <div className="flex w-full flex-col items-start gap-2 text-start align-baseline">
                {!inline && <span className="px-0.5 text-left text-sm">{formatMessage(tableMessages.to)}</span>}
                <SingleDatePicker
                    className="w-full"
                    buttonValue={formatDate(beforeDate)}
                    value={new Date(beforeDate.getTime() - beforeDate.getTimezoneOffset() * 60 * 1000)}
                    onChange={(date) => {
                        if (!date) return;

                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);

                        utcDate.setHours(beforeDate.getHours(), beforeDate.getMinutes());

                        let newAfterDate = afterDate;
                        if (afterDate.getTime() > utcDate.getTime()) {
                            newAfterDate = new Date(utcDate);
                        }

                        setValue({ afterDate: newAfterDate, beforeDate: utcDate });
                        if (!setLabel) return;
                        setLabel(`${formatDate(afterDate)} - ${formatDate(utcDate)}`);
                    }}
                />
                {enableTime && (
                    <Input
                        type="time"
                        className="justify-center p-2 text-sm"
                        defaultValue={defaultBeforeTime}
                        onChange={(e) => {
                            const [hours, minutes] = e.target.value.split(':').map(Number);
                            const newBeforeDate = new Date(beforeDate);
                            newBeforeDate.setHours(hours, minutes);

                            if (afterDate.getTime() > newBeforeDate.getTime()) return;

                            setValue({ afterDate, beforeDate: newBeforeDate });
                            if (!setLabel) return;
                            setLabel(`${formatDate(afterDate)} - ${formatDate(beforeDate)}`);
                        }}
                    />
                )}
            </div>
        </div>
    );
}
